import type { SVGAttributes } from 'react'

/**
 * Base icon interface for all SVG icons
 * Provides consistent prop typing for icon components
 */
export interface IconProps extends SVGAttributes<SVGSVGElement> {
  /**
   * CSS class names for the icon element.
   * Inherited from SVGAttributes but declared explicitly for clarity.
   */
  className?: string
  /**
   * Whether the icon should be hidden from screen readers
   * @default true
   */
  ariaHidden?: boolean
  /**
   * Size class for the icon (Tailwind classes)
   * @default undefined (use className instead for full control)
   */
  size?: string
  /**
   * SVG stroke-width — forwarded explicitly so icons can expose it as a
   * prop with a per-icon default (e.g. strokeWidth={1.5}).
   */
  strokeWidth?: number | string
}

/**
 * Base SVG props helper to merge common icon props
 * 
 * Provides universal SVG defaults (aria-hidden, className, fill, viewBox).
 * Does NOT set stroke or strokeWidth — each icon should declare its own
 * rendering style (stroke vs fill) explicitly on its paths or SVG element.
 * 
 * @example
 * ```tsx
 * // Stroke-based icon — set stroke on <svg> or <path>
 * export function ChevronIcon(props: IconProps) {
 *   return (
 *     <svg {...getIconProps(props)} viewBox="0 0 7 12">
 *       <path d="..." stroke="currentColor" strokeWidth="1.25" />
 *     </svg>
 *   )
 * }
 * 
 * // Fill-based icon — set fill on <path>
 * export function CalendarIcon(props: IconProps) {
 *   return (
 *     <svg {...getIconProps(props)} viewBox="0 0 15 17">
 *       <path d="..." fill="currentColor" />
 *     </svg>
 *   )
 * }
 * ```
 */
/**
 * Extended icon props for icons that support CSS rotation.
 *
 * Use this instead of a one-off interface when an icon needs a `rotation`
 * prop that maps to a CSS `transform: rotate(Ndeg)`. The value is in degrees
 * and is applied via inline `style`, so it supports fractional values and
 * runtime-computed angles.
 *
 * @example
 * ```tsx
 * export interface ArrowIconProps extends RotatableIconProps {}
 *
 * export function ArrowIcon({ rotation = 0, style, ...props }: ArrowIconProps) {
 *   return (
 *     <svg
 *       {...getIconProps(props)}
 *       style={{ ...style, transform: `rotate(${rotation}deg)` }}
 *     >
 *       ...
 *     </svg>
 *   )
 * }
 * ```
 */
export interface RotatableIconProps extends IconProps {
  /**
   * Rotation angle in degrees applied via inline CSS `transform: rotate(Ndeg)`.
   * Supports fractional values and negative angles.
   * @default 0
   */
  rotation?: number
}

export function getIconProps(
  { ariaHidden = true, className = '', size, ...props }: IconProps = {}
): SVGAttributes<SVGSVGElement> {
  return {
    'aria-hidden': ariaHidden,
    className: size ? `${size} ${className}`.trim() : className,
    fill: 'none',
    viewBox: '0 0 24 24',
    ...props,
  }
}
